import React from 'react';
import { Navigate } from 'react-router-dom';
import { useFeatureFlag, useFeatureFlagsReady } from '../../hooks/useFeatureFlag';

interface RequireFeatureFlagProps {
  /** Flag name from `FEATURE_FLAG_NAMES`. */
  flag: string;
  /** Where to send the user when the flag is off. Defaults to `/project`. */
  fallback?: string;
  children: React.ReactNode;
}

/**
 * Route-element wrapper that hides a route behind a company feature flag.
 * When the flag is off the user is redirected to `fallback` (default
 * `/project`).
 *
 * Usage in router config:
 *
 *     {
 *       path: 'lookup-library',
 *       element: (
 *         <RequireFeatureFlag flag={FEATURE_FLAG_NAMES.LOOKUP_TABLE}>
 *           <LookupLibrary />
 *         </RequireFeatureFlag>
 *       )
 *     }
 *
 * Unused today — provided so future lookup-specific routes have a one-line
 * gating primitive instead of repeating the redirect pattern inline.
 */
export const RequireFeatureFlag: React.FC<RequireFeatureFlagProps> = ({
  flag,
  fallback = '/project',
  children
}) => {
  const enabled = useFeatureFlag(flag);
  const ready = useFeatureFlagsReady();

  /* The flag reads false while the company's features are in flight, and a redirect on that answer
     throws out the very people the route is for — deep links and reloads, which is how anyone
     arrives at a stage page. Wait for the answer, then act on it. */
  if (!ready) return null;
  if (!enabled) return <Navigate to={fallback} replace />;
  return <>{children}</>;
};

export default RequireFeatureFlag;
